import {
  type LoadWidgetProps,
  createTemporaryActionQueue as resetTemporaryActionQueue,
} from '@asurion-core/widget-client/lib/package';
import { type WidgetActions } from '@asurion-core/widget-client/lib/types/WidgetVariant';
import { type Widget } from '@automation-suite/shared-clients-widget-variant';
import {
  type Options as EmbeddedOptions,
  WidgetVariantEmbedded,
} from '@automation-suite/shared-clients-widget-variant-embedded';
import {
  type Options as FabOptions,
  WidgetVariantFloatingActionButton,
} from '@automation-suite/shared-clients-widget-variant-fab';
import {
  type Options as FacOptions,
  WidgetVariantFloatingActionCard,
} from '@automation-suite/shared-clients-widget-variant-fac';
import {
  type Options as FicOptions,
  WidgetVariantFloatingInputCard,
} from '@automation-suite/shared-clients-widget-variant-fic';
import { dispatcher } from '@automation-suite/widget-loader-clients-analytics-dispatcher';
import { getUserTimezone, parseSearchParams } from '@automation-suite/shared-utils-helpers';
import { shouldRecreateWidget } from './utils/shouldRecreateWidget';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let widget: Widget<any, any, any, any> | undefined;

declare global {
  interface Window {
    __CHAT_SDK?: {
      hideChatBubble?: () => void;
    };
    SimplrChat?: {
      hide?: () => void;
    };
  }
}

export const createWidget = async <T extends WidgetActions>({
  flowSystemId,
  id,
  namespace = '_si_widget',
  variant,
  variantOptionOverrides,
  showStateDebugger,
  forceRestartOnInit,
  context = {},
  trackingParameters = {},
  parentElementId,
  language,
  loaderUrl,
  pushNotification,
  targetExpertId,
}: LoadWidgetProps) => {
  const currentProps = {
    id,
    flowSystemId,
    namespace,
    variant,
    variantOptionOverrides,
    showStateDebugger,
    forceRestartOnInit,
    context,
    trackingParameters,
    parentElementId,
    language,
    loaderUrl,
    pushNotification,
    targetExpertId,
  };

  if (!shouldRecreateWidget(currentProps, widget)) {
    return;
  }

  if (widget) {
    resetTemporaryActionQueue(namespace);
    await widget.destroy();

    if (window?.SimplrChat?.hide) {
      window.SimplrChat.hide();
    }
  }
  const optionOverrides = variantOptionOverrides || {};
  const sharedWidgetParams = {
    analyticsDispatcher: dispatcher,
    namespace,
    loaderUrl,
    configs: {
      language,
      parentElementId,
      simplr: {
        flowSystemId,
        widgetId: id,
        trackingParameters,
        pushNotification,
        moreTicketInfo: {
          userOriginUrl: window.location.href,
          hostPageUrl: window.location.href,
          hostPageOrigin: window.location.origin,
          hostPagePath: window.location.pathname,
          hostPageQueryParams: parseSearchParams(window.location.search),
          userTimezone: getUserTimezone(),
          consumerContext: context,
          oneWidget: false,
          ...(targetExpertId && { targetExpertId }),
        },
        showStateDebugger,
        forceRestartOnInit,
      },
    },
  };

  switch (variant) {
    case 'floating-action-button': {
      sharedWidgetParams.configs.simplr.moreTicketInfo.oneWidget = true;
      widget = new WidgetVariantFloatingActionButton({
        ...sharedWidgetParams,
        optionOverrides: optionOverrides as FabOptions,
      });
      break;
    }
    case 'floating-action-card': {
      sharedWidgetParams.configs.simplr.moreTicketInfo.oneWidget = true;
      widget = new WidgetVariantFloatingActionCard({
        ...sharedWidgetParams,
        optionOverrides: optionOverrides as FacOptions,
      });
      break;
    }
    case 'embedded': {
      sharedWidgetParams.configs.simplr.moreTicketInfo.oneWidget = true;
      widget = new WidgetVariantEmbedded({
        ...sharedWidgetParams,
        optionOverrides: optionOverrides as EmbeddedOptions,
      });
      break;
    }
    case 'floating-input-card': {
      sharedWidgetParams.configs.simplr.moreTicketInfo.oneWidget = true;
      widget = new WidgetVariantFloatingInputCard({
        ...sharedWidgetParams,
        optionOverrides: optionOverrides as FicOptions,
      });
      break;
    }
    default:
      throw new Error(`Variant (${variant}) is not currently supported.`);
  }

  await widget.init();

  const actionQueue =
    (
      (window as unknown as Record<string, unknown>)[namespace] as {
        q: Array<[keyof T, unknown[]]>;
      }
    )?.q ?? [];

  for (let i = 0; i < actionQueue.length; i++) {
    const [action, ...args] = actionQueue[i];
    await widget.execute(action, ...args);
  }

  (window as unknown as Record<string, unknown>)[namespace] = async (action: keyof T, ...args: unknown[]) =>
    widget && (await widget.execute(action, ...args));
};
